import Raven from 'frontend/utils/raven';
import { Module } from 'vuex';

import {
  ActiveExperimentGetter,
  ClassroomIntentKeyForSignup,
  PrivacyProperties,
  FeatureSwitches,
  GlobalState,
  RootState,
  User,
  unreachableCase,
  StreakStats,
  Optional,
} from 'frontend/types';

import { inExperimentGetter, SPONSOR_PAGES } from 'frontend/utils/experiment';
import { updateProfileFlag } from 'frontend/utils/flags';
import { SponsorLandingPagesTreatment } from 'frontend/types/landing/marketing';
import { Discount } from 'frontend/types/premium';

const globalInfo: Module<GlobalState, RootState> = {
  namespaced: true,
  state: () => ({
    analytics: undefined,
    experiments: undefined,
    feature_switches: undefined,
    user: undefined,
    privacy_properties: undefined,
    join_classroom_intent_key_for_signup: undefined,
    loaded_profitwell_snippet: undefined,
    posted_profitwell_user_email: undefined,
    google_oauth_client_id: undefined,
    discount: undefined,
    front_token_campgrounds_authors: undefined,
    algolia_proxy_url: undefined,
  }),
  mutations: {
    setValue(state: GlobalState, value: GlobalState): void {
      Object.assign(state, value);

      // Add user debug info for Sentry
      if (state.user) {
        Raven.setTagsContext({
          anonymous: state.user.is_authenticated === true,
          b2: state.user.has_brilliant_premium === true,
        });
        if (state.user.is_authenticated) {
          Raven.setUserContext({
            id: state.user.user_id,
          });
        }
      }
    },
    clearTrackedEvents(state: GlobalState) {
      if (state.analytics) {
        state.analytics.javascript_tracked_events = [];
      }
    },
    setJoinClassroomIntentKeyForSignup(
      state: GlobalState,
      joinClassroomIntentKeyForSignup: ClassroomIntentKeyForSignup | undefined,
    ) {
      state.join_classroom_intent_key_for_signup = joinClassroomIntentKeyForSignup;
    },
    closePrivacyBanner(state: GlobalState) {
      if (state.privacy_properties) {
        state.privacy_properties.viewed_privacy_banner = true;
      }
    },
    toggleThirdPartyCookies(state: GlobalState, enabled: boolean) {
      if (state.privacy_properties) {
        state.privacy_properties.third_party_cookies_disabled = !enabled;
      }
    },
    loadProfitwellSnippet(state: GlobalState) {
      state.loaded_profitwell_snippet = true;
    },
    setProfitwellUserEmailAsPosted(state: GlobalState) {
      state.posted_profitwell_user_email = true;
    },
    setUser(state: GlobalState, user: User) {
      state.user = user;
    },
    setStreakData(state: GlobalState, streakData: StreakStats) {
      if (state.user) {
        state.user.streak = streakData;
      }
    },
  },
  actions: {
    clearTrackedEvents({ commit }) {
      commit('clearTrackedEvents');
    },
    setJoinClassroomIntentKeyForSignup(
      { commit },
      joinClassroomIntentKeyForSignup: ClassroomIntentKeyForSignup | undefined,
    ) {
      commit('setJoinClassroomIntentKeyForSignup', joinClassroomIntentKeyForSignup);
    },
    async closePrivacyBanner({ commit }) {
      await updateProfileFlag('viewed_privacy_banner');
      commit('closePrivacyBanner');
    },
    async toggleThirdPartyCookies({ commit }, enabled: boolean) {
      await updateProfileFlag('third_party_cookies_disabled', !enabled);
      commit('toggleThirdPartyCookies', enabled);
    },
    loadProfitwellSnippet({ commit }) {
      commit('loadProfitwellSnippet');
    },
    setProfitwellUserEmailAsPosted({ commit }) {
      commit('setProfitwellUserEmailAsPosted');
    },
    setUser({ commit }, user: User) {
      commit('setUser', user);
    },
    setStreakData({ commit }, streakData: StreakStats) {
      commit('setStreakData', streakData);
    },
  },
  getters: {
    inExperiment: state => inExperimentGetter(state.experiments),
    featureSwitches(state): FeatureSwitches | undefined {
      return state.feature_switches;
    },
    privacyProperties(state): PrivacyProperties | undefined {
      return state.privacy_properties;
    },
    userFullName(state): string {
      return state.user ? `${state.user.first_name} ${state.user.last_name}` : '';
    },
    joinClassroomIntentKeyForSignup(state): ClassroomIntentKeyForSignup | undefined {
      return state.join_classroom_intent_key_for_signup;
    },
    isAuthenticated(state): boolean {
      return !!state.user && state.user.is_authenticated === true;
    },
    googleOauthClientId(state): string | undefined {
      return state.google_oauth_client_id;
    },
    frontTokenCampgroundsAuthors(state): string | undefined {
      return state.front_token_campgrounds_authors;
    },
    algoliaProxyUrl(state): string | undefined {
      return state.algolia_proxy_url;
    },
    sponsorLandingPagesTreatment(state, getters): SponsorLandingPagesTreatment {
      // Have to call inExperiment to track the experiment
      getters.inExperiment(SPONSOR_PAGES.EXPERIMENT, 'control');
      const experiment = state.experiments && state.experiments[SPONSOR_PAGES.EXPERIMENT];
      if (experiment) {
        const treatment: SponsorLandingPagesTreatment =
          experiment.treatment_name as SponsorLandingPagesTreatment;
        switch (treatment) {
          case 'control':
          case 'regular_nux_updated_paywall':
          case 'short_paywall':
          case 'long_paywall':
            return treatment;
          default:
            unreachableCase(treatment);
        }
      }
      return 'control';
    },
    getActiveTreatment:
      (state, getters): ActiveExperimentGetter =>
      (experimentName: string) => {
        const treatment =
          state.experiments && state.experiments[experimentName]
            ? state.experiments[experimentName].treatment_name
            : 'control';
        getters.inExperiment(experimentName, treatment); // to track the experiment
        return treatment;
      },
    streakData(state): StreakStats | undefined {
      return state.user?.streak;
    },
    discount(state): Optional<Discount> {
      return state.discount;
    },
  },
};

export default globalInfo;
